import { useSelector } from "react-redux";
import styles from "./RulesList.module.scss";

const RuleSummary = ({ criteria, maxLength = 80 }) => {
  const { rfqSources } = useSelector((state) => state.rfqNotifications);

  const formatStatesList = (states) => {
    if (!states) return "Any";
    
    // Handle both comma-separated strings and arrays
    let statesArray;
    if (typeof states === 'string') {
      statesArray = states.split(',').map(state => state.trim()).filter(state => state);
    } else if (Array.isArray(states)) {
      statesArray = states.filter(state => state);
    } else {
      return "Any";
    }
    
    if (statesArray.length === 0) return "Any";
    if (statesArray.length === 1) return statesArray[0];
    if (statesArray.length <= 3) return statesArray.join(", ");
    return `${statesArray.slice(0, 2).join(", ")} +${statesArray.length - 2} more`;
  };

  const formatSource = (sources) => {
    if (!sources || (Array.isArray(sources) && sources.length === 0)) return "Any source";
    
    // Handle both legacy single source and new array of sources
    const sourcesArray = Array.isArray(sources) ? sources : [sources];
    
    // Create dynamic source mapping from Redux data
    const sourceMap = {};
    if (rfqSources && Array.isArray(rfqSources)) {
      rfqSources.forEach(source => {
        sourceMap[source.id] = source.name;
        // Also handle legacy string codes if they exist in the source data
        if (source.code) {
          sourceMap[source.code] = source.name;
        }
      });
    }
    
    // Fallback legacy mappings for backward compatibility
    const legacyMap = {
      sylectus: "Sylectus",
      dat: "DAT", 
      load_link: "LoadLink",
      both: "All sources"
    };
    
    const formattedSources = sourcesArray.map(source => 
      sourceMap[source] || legacyMap[source] || `Source ${source}`
    );
    
    if (formattedSources.length === 1) {
      return formattedSources[0];
    } else if (formattedSources.length <= 2) {
      return formattedSources.join(" & ");
    } else {
      return `${formattedSources[0]} +${formattedSources.length - 1} more`;
    }
  };

  const formatMiles = (minMiles, maxMiles) => {
    if (!minMiles && !maxMiles) return "";
    if (minMiles && maxMiles) return ` • ${minMiles}-${maxMiles} miles`;
    if (minMiles) return ` • ${minMiles}+ miles`;
    if (maxMiles) return ` • up to ${maxMiles} miles`;
    return "";
  };

  const pickupStates = formatStatesList(criteria.pick_up_states_only);
  const deliveryStates = formatStatesList(criteria.delivery_states_only);
  const source = formatSource(criteria.rfq_sources);
  const miles = formatMiles(criteria.min_total_miles, criteria.max_total_miles);

  const summaryParts = [];
  
  if (pickupStates !== "Any" || deliveryStates !== "Any") {
    summaryParts.push(`${pickupStates} → ${deliveryStates}`);
  }
  
  if (source !== "Any source") {
    summaryParts.push(`via ${source}`);
  }
  
  if (miles) {
    summaryParts.push(miles.replace(' • ', ''));
  }

  let summary = summaryParts.join(' • ') || "No specific criteria";
  
  if (summary.length > maxLength) {
    summary = summary.substring(0, maxLength - 3) + "...";
  }

  const hasCustomerRestrictions = criteria.all_customers === false && (criteria.customers_count > 0);
  const hasContactRestrictions = criteria.all_customer_contacts === false && (criteria.customer_contacts_count > 0);
  const hasAnyRestrictions = hasCustomerRestrictions || hasContactRestrictions;

  const restrictionParts = [];
  if (hasCustomerRestrictions) {
    restrictionParts.push(`${criteria.customers_count} customer${criteria.customers_count !== 1 ? 's' : ''}`);
  }
  if (hasContactRestrictions) {
    restrictionParts.push(`${criteria.customer_contacts_count} contact${criteria.customer_contacts_count !== 1 ? 's' : ''}`);
  }

  return (
    <div className={styles.ruleSummary}>
      <span className={styles.summaryText}>{summary}</span>
      {(criteria.match_pick_up_and_delivery_states || hasAnyRestrictions) && (
        <div className={styles.summaryNote}>
          {criteria.match_pick_up_and_delivery_states && (
            <span>Both pickup and delivery required</span>
          )}
          {hasAnyRestrictions && (
            <span>
              {criteria.match_pick_up_and_delivery_states ? ' • ' : ''}
              {restrictionParts.join(', ')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default RuleSummary;